import { useEffect, useMemo, useState } from 'react';
import { Accordion, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import api from 'services/api';

const hasValue = (value: unknown): boolean => value !== null && value !== undefined;

const parseMaybeJson = (value: any) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const formatJsonBlock = (value: any) => {
  if (!hasValue(value)) return 'Not available.';
  const parsed = parseMaybeJson(value);
  if (typeof parsed === 'string') return parsed;
  try {
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(parsed);
  }
};

const formatCellValue = (value: any) => {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const toLookupKey = (value: any): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value.trim() !== '') return value.trim();
  return null;
};

const isPlainObject = (value: any): boolean => value !== null && typeof value === 'object' && !Array.isArray(value);

const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keysA = Object.keys(a).sort();
    const keysB = Object.keys(b).sort();
    if (!deepEqual(keysA, keysB)) return false;
    for (const key of keysA) {
      if (!deepEqual(a[key], b[key])) return false;
    }
    return true;
  }
  return false;
};

type DiffEntry = {
  path: string;
  executed: any;
  current: any;
};

type PropertyField = {
  label: string;
  paths: string[];
};

type PropertyDiffEntry = {
  label: string;
  executedPath: string | null;
  currentPath: string | null;
  executed: any;
  current: any;
  changed: boolean;
  present: boolean;
};

const PROPERTY_FIELDS: PropertyField[] = [
  { label: 'Owner User', paths: ['config.owner_user', 'owner_user'] },
  { label: 'Owner Role', paths: ['config.owner_role', 'owner_role'] },
  {
    label: 'Default Queue',
    paths: ['default_queue_path', 'queue_path', 'default_queue_name', 'default_queue_id'],
  },
  { label: 'Zone', paths: ['zone_name', 'zone_id'] },
  { label: 'Folder', paths: ['folder', 'folder_name', 'folder_id'] },
  { label: 'Locks', paths: ['locks'] },
  { label: 'Retention Policies', paths: ['retention_policies'] },
  {
    label: 'Restrict To Scheduling Window',
    paths: ['restrict_to_scheduling_window'],
  },
  { label: 'Auto Restart Enabled', paths: ['auto_restart_enabled'] },
  { label: 'Auto Restart Amount', paths: ['auto_restart_amount'] },
  {
    label: 'Auto Restart Delay Seconds',
    paths: ['auto_restart_delay_seconds'],
  },
  { label: 'Restart On Return Codes', paths: ['restart_on_return_codes'] },
  {
    label: 'Runtime Limit Min',
    paths: ['runtime_limit_min', 'runtime_limit_min_hhmmss'],
  },
  {
    label: 'Runtime Limit Max',
    paths: ['runtime_limit_max', 'runtime_limit_max_hhmmss'],
  },
  { label: 'Runtime Limit Event', paths: ['runtime_limit_event'] },
  { label: 'Runtime Limit Terminate', paths: ['runtime_limit_terminate'] },
  { label: 'Handles', paths: ['handles'] },
  { label: 'Flow On Error', paths: ['config.on_error'] },
  {
    label: 'Workflow Node Definition',
    paths: ['config.workflow_node_definition'],
  },
  { label: 'Description', paths: ['description'] },
];

const getDiffEntries = (executed: any, current: any, basePath = ''): DiffEntry[] => {
  if (deepEqual(executed, current)) return [];

  if (Array.isArray(executed) && Array.isArray(current)) {
    const maxLength = Math.max(executed.length, current.length);
    const changes: DiffEntry[] = [];
    for (let i = 0; i < maxLength; i += 1) {
      const nextPath = `${basePath}[${i}]`;
      changes.push(...getDiffEntries(executed[i], current[i], nextPath));
    }
    return changes;
  }

  if (isPlainObject(executed) && isPlainObject(current)) {
    const keys = Array.from(new Set([...Object.keys(executed), ...Object.keys(current)])).sort();
    const changes: DiffEntry[] = [];
    for (const key of keys) {
      const nextPath = basePath ? `${basePath}.${key}` : key;
      changes.push(...getDiffEntries(executed[key], current[key], nextPath));
    }
    return changes;
  }

  return [{ path: basePath || 'value', executed, current }];
};

const getPathParts = (path: string) => path.split(/[.[\]]/).filter(Boolean);

const PROPERTY_ROOT_KEYS = new Set([
  'description',
  'default_queue_path',
  'default_queue_name',
  'default_queue_id',
  'queue_path',
  'zone_name',
  'zone_id',
  'folder',
  'folder_name',
  'folder_id',
  'locks',
  'retention_policies',
  'restrict_to_scheduling_window',
  'auto_restart_enabled',
  'auto_restart_amount',
  'auto_restart_delay_seconds',
  'restart_on_return_codes',
  'runtime_limit_min',
  'runtime_limit_max',
  'runtime_limit_event',
  'runtime_limit_terminate',
  'handles',
]);

const PROPERTY_CONFIG_KEYS = new Set(['owner_user', 'owner_role', 'on_error', 'workflow_node_definition']);

const getSection = (path: string) => {
  const parts = getPathParts(path);
  const rootKey = parts[0] || '';
  const childKey = parts[1] || '';

  if (rootKey === 'parameters') return 'Parameters';
  if (rootKey === 'wait_events' || rootKey === 'raise_events') return 'Events';

  if (rootKey === 'config') {
    if (PROPERTY_CONFIG_KEYS.has(childKey)) return 'Properties';
    return 'Definition';
  }

  if (
    rootKey === 'name' ||
    rootKey === 'type' ||
    rootKey === 'sub_type' ||
    rootKey === 'documentation' ||
    rootKey === 'child_nodes' ||
    rootKey === 'child_edges' ||
    rootKey === 'version'
  ) {
    return 'Definition';
  }

  if (PROPERTY_ROOT_KEYS.has(rootKey)) return 'Properties';
  return 'Properties';
};

const getValueByPath = (obj: any, path: string) => {
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (!hasValue(current) || typeof current !== 'object') return null;
    current = current[key];
  }
  return current;
};

const getFirstValueByPaths = (obj: any, paths: string[]) => {
  for (const path of paths) {
    const value = getValueByPath(obj, path);
    if (hasValue(value)) return { path, value };
  }
  return { path: null, value: null };
};

const getPropertyDiffEntries = (executedSnapshot: any, currentSnapshot: any): PropertyDiffEntry[] =>
  PROPERTY_FIELDS.map((field) => {
    const executed = getFirstValueByPaths(executedSnapshot, field.paths);
    const current = getFirstValueByPaths(currentSnapshot, field.paths);
    const present = hasValue(executed.path) || hasValue(current.path);
    const changed = present && !deepEqual(executed.value, current.value);

    return {
      label: field.label,
      executedPath: executed.path,
      currentPath: current.path,
      executed: executed.value,
      current: current.value,
      changed,
      present,
    };
  });

const extractScriptFromSnapshot = (snapshot: any): string | null => {
  if (!hasValue(snapshot)) return null;
  if (typeof snapshot === 'string' && snapshot.trim() !== '') return snapshot;

  const preferredPaths = ['script', 'config.script', 'node_data.script', 'config.node_data.script'];
  for (const path of preferredPaths) {
    const value = getValueByPath(snapshot, path);
    if (typeof value === 'string' && value.trim() !== '') return value;
  }

  const visited = new WeakSet<object>();
  const visit = (value: any): string | null => {
    if (!hasValue(value)) return null;

    if (Array.isArray(value)) {
      for (const item of value) {
        const found = visit(item);
        if (found) return found;
      }
      return null;
    }

    if (!isPlainObject(value)) return null;
    if (visited.has(value)) return null;
    visited.add(value);

    const entries = Object.entries(value);
    for (const [key, child] of entries) {
      if (typeof child === 'string' && /script/i.test(key) && child.trim() !== '') {
        return child;
      }
    }

    for (const [, child] of entries) {
      const found = visit(child);
      if (found) return found;
    }

    return null;
  };

  return visit(snapshot);
};

const scrollPaneStyle = (maxHeight: number | string) => ({
  maxHeight,
  overflow: 'auto',
  border: '1px solid #dee2e6',
  borderRadius: 6,
  background: '#f8f9fa',
  padding: '0.75rem',
});

const renderCompareBlock = ({
  executedTitle,
  currentTitle,
  executedValue,
  currentValue,
  maxHeight,
}: {
  executedTitle: string;
  currentTitle: string;
  executedValue: string;
  currentValue: string;
  maxHeight: number | string;
}) => (
  <Row className="g-2">
    <Col md={6}>
      <div className="fw-semibold mb-1">{executedTitle}</div>
      <div style={scrollPaneStyle(maxHeight)}>
        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {executedValue}
        </pre>
      </div>
    </Col>
    <Col md={6}>
      <div className="fw-semibold mb-1">{currentTitle}</div>
      <div style={scrollPaneStyle(maxHeight)}>
        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {currentValue}
        </pre>
      </div>
    </Col>
  </Row>
);

const SECTION_ORDER = ['Definition', 'Parameters', 'Events', 'Properties'] as const;
const MAX_VISIBLE_CHANGES = 500;

const DefinitionVersion = ({ state }: { state: any }) => {
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [showPropertiesModal, setShowPropertiesModal] = useState(false);
  const [usersById, setUsersById] = useState<Record<string, string>>({});
  const [rolesById, setRolesById] = useState<Record<string, string>>({});

  const {
    name = '',
    node_definition_id,
    definition_id,
    definition_version,
    current_definition_version,
    definition_changed_after_execution,
    executed_definition_snapshot,
    current_definition_snapshot,
    executed_definition_config,
    current_definition_config,
  } = state;

  const effectiveDefinitionId = node_definition_id ?? definition_id ?? null;

  const executedSnapshotRaw = executed_definition_snapshot ?? (hasValue(executed_definition_config) ? { config: executed_definition_config } : null);
  const currentSnapshotRaw = current_definition_snapshot ?? (hasValue(current_definition_config) ? { config: current_definition_config } : null);

  const executedSnapshot = parseMaybeJson(executedSnapshotRaw);
  const currentSnapshot = parseMaybeJson(currentSnapshotRaw);

  useEffect(() => {
    let alive = true;

    const fetchOwnershipData = async () => {
      try {
        const [usersResponse, rolesResponse] = await Promise.all([api.get('/users'), api.get('/roles')]);

        if (!alive) return;

        const users = Array.isArray(usersResponse.data?.users) ? usersResponse.data.users : [];
        const roles = Array.isArray(rolesResponse.data?.roles) ? rolesResponse.data.roles : Array.isArray(rolesResponse.data) ? rolesResponse.data : [];

        const usersMap = users.reduce((acc: Record<string, string>, user: any) => {
          const key = toLookupKey(user?.user_id);
          const label = typeof user?.username === 'string' ? user.username : null;
          if (key && label) acc[key] = label;
          return acc;
        }, {});

        const rolesMap = roles.reduce((acc: Record<string, string>, role: any) => {
          const key = toLookupKey(role?.role_id);
          const label = typeof role?.name === 'string' ? role.name : null;
          if (key && label) acc[key] = label;
          return acc;
        }, {});

        setUsersById(usersMap);
        setRolesById(rolesMap);
      } catch {
        if (!alive) return;
        setUsersById({});
        setRolesById({});
      }
    };

    fetchOwnershipData();

    return () => {
      alive = false;
    };
  }, []);

  const getOwnerKindFromPath = (path: string | null | undefined) => {
    if (!path) return null;
    if (path.includes('owner_user')) return 'user';
    if (path.includes('owner_role')) return 'role';
    return null;
  };

  const getOwnerKindFromLabel = (label: string | null | undefined) => {
    if (!label) return null;
    if (label === 'Owner User') return 'user';
    if (label === 'Owner Role') return 'role';
    return null;
  };

  const resolveOwnershipValue = (value: any, ownerKind: 'user' | 'role' | null | undefined) => {
    if (!ownerKind) return value;
    const key = toLookupKey(value);
    if (!key) return value;

    if (ownerKind === 'user') {
      return usersById[key] ?? value;
    }
    return rolesById[key] ?? value;
  };

  const definitionChanged =
    typeof definition_changed_after_execution === 'boolean'
      ? definition_changed_after_execution
      : hasValue(definition_version) && hasValue(current_definition_version) && definition_version !== current_definition_version;

  const canDiff = hasValue(executedSnapshot) && hasValue(currentSnapshot);
  const allChanges = useMemo(
    () => (canDiff ? getDiffEntries(executedSnapshot, currentSnapshot).sort((a, b) => a.path.localeCompare(b.path)) : []),
    [canDiff, executedSnapshot, currentSnapshot]
  );
  const visibleChanges = allChanges.slice(0, MAX_VISIBLE_CHANGES);

  const groupedChanges: Record<string, DiffEntry[]> = {
    Definition: [],
    Parameters: [],
    Events: [],
    Properties: [],
  };
  for (const change of visibleChanges) {
    groupedChanges[getSection(change.path)].push(change);
  }

  const propertyEntries = useMemo(() => getPropertyDiffEntries(executedSnapshot, currentSnapshot), [executedSnapshot, currentSnapshot]);
  const changedPropertyEntries = propertyEntries.filter((entry) => entry.present && entry.changed);

  const executedPropertySnapshot = useMemo(
    () =>
      propertyEntries.reduce(
        (acc, entry) => {
          if (!entry.present) return acc;
          const ownerKind = getOwnerKindFromLabel(entry.label);
          acc[entry.label] = resolveOwnershipValue(entry.executed, ownerKind);
          return acc;
        },
        {} as Record<string, any>
      ),
    [propertyEntries, rolesById, usersById]
  );

  const currentPropertySnapshot = useMemo(
    () =>
      propertyEntries.reduce(
        (acc, entry) => {
          if (!entry.present) return acc;
          const ownerKind = getOwnerKindFromLabel(entry.label);
          acc[entry.label] = resolveOwnershipValue(entry.current, ownerKind);
          return acc;
        },
        {} as Record<string, any>
      ),
    [propertyEntries, rolesById, usersById]
  );

  const executedScript = useMemo(() => extractScriptFromSnapshot(executedSnapshot), [executedSnapshot]);
  const currentScript = useMemo(() => extractScriptFromSnapshot(currentSnapshot), [currentSnapshot]);

  const hasScriptCompare = hasValue(executedScript) || hasValue(currentScript);
  const scriptChanged = hasScriptCompare && executedScript !== currentScript;

  const hasAnySnapshot = hasValue(executedSnapshot) || hasValue(currentSnapshot);
  const executedScriptText = executedScript ?? 'Not available.';
  const currentScriptText = currentScript ?? 'Not available.';
  const executedSnapshotText = useMemo(() => formatJsonBlock(executedSnapshot), [executedSnapshot]);
  const currentSnapshotText = useMemo(() => formatJsonBlock(currentSnapshot), [currentSnapshot]);
  const executedPropertiesText = useMemo(() => formatJsonBlock(executedPropertySnapshot), [executedPropertySnapshot]);
  const currentPropertiesText = useMemo(() => formatJsonBlock(currentPropertySnapshot), [currentPropertySnapshot]);

  if (!hasAnySnapshot && !hasValue(effectiveDefinitionId)) {
    return <div className="text-muted">No definition available.</div>;
  }

  return (
    <>
      {definitionChanged && <div className="mb-2 text-warning">This instance ran with an older definition version.</div>}

      <Table size="sm" className="mb-3" striped bordered hover>
        <tbody>
          <tr>
            <td>Definition ID</td>
            <td>{effectiveDefinitionId ?? 'Unknown'}</td>
          </tr>
          <tr>
            <td>Definition Name</td>
            <td>{name || 'Unknown'}</td>
          </tr>
          <tr>
            <td>Executed Version</td>
            <td>{definition_version ?? 'Unknown'}</td>
          </tr>
          <tr>
            <td>Current Version</td>
            <td>{current_definition_version ?? 'Unknown'}</td>
          </tr>
        </tbody>
      </Table>

      <Accordion alwaysOpen>
        <Accordion.Item eventKey="changed-values">
          <Accordion.Header>Changed Values ({allChanges.length})</Accordion.Header>
          <Accordion.Body>
            {!canDiff && <div className="mb-3 text-muted">Full comparison is not available for this instance.</div>}

            {canDiff && allChanges.length === 0 && <div className="mb-3 text-muted">No changes detected between executed and current definitions.</div>}

            {canDiff && allChanges.length > 0 && (
              <div className="mb-2">
                {allChanges.length > MAX_VISIBLE_CHANGES && <div className="text-muted mb-2">Showing first {MAX_VISIBLE_CHANGES} changes.</div>}

                {SECTION_ORDER.map((section) => {
                  const changes = groupedChanges[section];
                  if (!changes.length) return null;

                  return (
                    <div key={section} className="mb-3">
                      <div className="fw-semibold mb-1">
                        {section} ({changes.length})
                      </div>
                      <Table size="sm" striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Path</th>
                            <th>Executed</th>
                            <th>Current</th>
                          </tr>
                        </thead>
                        <tbody>
                          {changes.map((change, idx) => (
                            <tr key={`${section}-${change.path}-${idx}`}>
                              <td
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  minWidth: 180,
                                }}
                              >
                                {change.path}
                              </td>
                              <td
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {formatCellValue(resolveOwnershipValue(change.executed, getOwnerKindFromPath(change.path)))}
                              </td>
                              <td
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {formatCellValue(resolveOwnershipValue(change.current, getOwnerKindFromPath(change.path)))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  );
                })}
              </div>
            )}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="properties">
          <Accordion.Header>Properties (Node Definition) ({changedPropertyEntries.length} changed)</Accordion.Header>
          <Accordion.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">Executed vs Current Properties</Form.Label>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setShowPropertiesModal(true)}
                disabled={!propertyEntries.some((entry) => entry.present)}
              >
                Open properties compare
              </Button>
            </div>

            {changedPropertyEntries.length === 0 && (
              <div className="mb-2 text-muted">No property changes detected (owner, role, queue, zone, folder, locks, runtime, etc.).</div>
            )}

            {changedPropertyEntries.length > 0 && (
              <Table size="sm" striped bordered hover responsive className="mb-3">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>Executed</th>
                    <th>Current</th>
                  </tr>
                </thead>
                <tbody>
                  {changedPropertyEntries.map((entry) => (
                    <tr key={entry.label}>
                      <td
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {entry.label}
                        {(entry.executedPath || entry.currentPath) && (
                          <div className="text-muted small">
                            {entry.executedPath || 'missing'}
                            {' -> '}
                            {entry.currentPath || 'missing'}
                          </div>
                        )}
                      </td>
                      <td
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {formatCellValue(resolveOwnershipValue(entry.executed, getOwnerKindFromLabel(entry.label)))}
                      </td>
                      <td
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {formatCellValue(resolveOwnershipValue(entry.current, getOwnerKindFromLabel(entry.label)))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {renderCompareBlock({
              executedTitle: 'Executed Properties Definition',
              currentTitle: 'Current Properties Definition',
              executedValue: executedPropertiesText,
              currentValue: currentPropertiesText,
              maxHeight: 240,
            })}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="script">
          <Accordion.Header>Executed vs Current Script</Accordion.Header>
          <Accordion.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">Script Compare</Form.Label>
              <Button size="sm" variant="outline-secondary" onClick={() => setShowScriptModal(true)} disabled={!hasScriptCompare}>
                Open script compare
              </Button>
            </div>

            {scriptChanged && <div className="mb-2 text-warning">Script changed since execution.</div>}
            {!hasScriptCompare && <div className="mb-2 text-muted">No script field detected in either definition.</div>}

            {renderCompareBlock({
              executedTitle: 'Executed Script',
              currentTitle: 'Current Script',
              executedValue: executedScriptText,
              currentValue: currentScriptText,
              maxHeight: 240,
            })}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="snapshot">
          <Accordion.Header>Executed vs Current Definition</Accordion.Header>
          <Accordion.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">Definition Compare</Form.Label>
              <Button size="sm" variant="outline-secondary" onClick={() => setShowSnapshotModal(true)} disabled={!hasAnySnapshot}>
                Open definition compare
              </Button>
            </div>

            {renderCompareBlock({
              executedTitle: 'Executed Definition',
              currentTitle: 'Current Definition',
              executedValue: executedSnapshotText,
              currentValue: currentSnapshotText,
              maxHeight: 280,
            })}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Modal show={showPropertiesModal} onHide={() => setShowPropertiesModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Executed vs Current Properties</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderCompareBlock({
            executedTitle: 'Executed Properties Definition',
            currentTitle: 'Current Properties Definition',
            executedValue: executedPropertiesText,
            currentValue: currentPropertiesText,
            maxHeight: '65vh',
          })}
        </Modal.Body>
      </Modal>

      <Modal show={showScriptModal} onHide={() => setShowScriptModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Executed vs Current Script</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderCompareBlock({
            executedTitle: 'Executed Script',
            currentTitle: 'Current Script',
            executedValue: executedScriptText,
            currentValue: currentScriptText,
            maxHeight: '65vh',
          })}
        </Modal.Body>
      </Modal>

      <Modal show={showSnapshotModal} onHide={() => setShowSnapshotModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Executed vs Current Definition</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderCompareBlock({
            executedTitle: 'Executed Definition',
            currentTitle: 'Current Definition',
            executedValue: executedSnapshotText,
            currentValue: currentSnapshotText,
            maxHeight: '65vh',
          })}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DefinitionVersion;
